import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useImageUrl } from '../api/useImageUrl';
import { useTasks } from '../api/useTasks';

/**
 * Labelling a crate: which product is this?
 *
 * A separate page from the anomaly annotator rather than a mode inside it,
 * because the two share almost nothing. That one asks "is this acceptable" and
 * gives you three buttons and a pencil. This one asks "which of eighty is it",
 * which is a search problem: nobody scans eighty names, they type three letters.
 *
 * The design constraint is the operator's ten seconds. Everything here serves
 * that: type to filter, arrows to move, Enter to commit, and the classes you
 * have used recently sit at the top because a shift rarely spans the whole
 * range — a deboning line runs hams all morning.
 *
 * When a model exists its prediction is preselected, so agreeing costs one key.
 * Disagreeing costs the same as it would have anyway. That asymmetry is the
 * whole point: the common case must be nearly free, or the labelling stops.
 */

interface Klass {
  id: number;
  code: string;
  name: string;
  family: string | null;
  images: number;
  active: boolean;
}

/**
 * One entry from the queue.
 *
 * This is an Inspection as the API returns it — I first wrote this view against
 * an invented shape with an `imageUrl` on it, and the image silently failed to
 * load. Images come from /api/images/<key> through useImageUrl, like everywhere
 * else, and the class fields are what the classifier wrote.
 */
interface Item {
  id: string;
  imageKey: string;
  classId?: number | null;
  classConfidence?: number | null;
  decision?: string | null;
}

export function ClassifyView() {
  const { products, taskFor } = useTasks();
  const classifyProducts = products.filter((p) => taskFor(p.id).usesClasses);

  const [productId, setProductId] = useState<number | null>(null);
  const [classes, setClasses] = useState<Klass[]>([]);
  const [queue, setQueue] = useState<Item[]>([]);
  const [index, setIndex] = useState(0);
  const [filter, setFilter] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [recent, setRecent] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(0);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (productId === null && classifyProducts.length > 0) setProductId(classifyProducts[0]!.id);
  }, [classifyProducts, productId]);

  useEffect(() => {
    if (!productId) return;
    fetch(`/api/products/${productId}/classes`)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows: Klass[]) => setClasses(rows.filter((c) => c.active)))
      .catch(() => undefined);
  }, [productId]);

  const loadQueue = useCallback(async () => {
    if (!productId) return;
    try {
      const res = await fetch(`/api/annotations/queue?productId=${productId}&limit=50`);
      if (!res.ok) {
        /**
         * Say which failure this is.
         *
         * "Could not load the queue" covered an expired session, a restarted
         * backend and a genuine fault alike — three problems with three
         * different fixes, and none of them the one the operator would guess.
         * The commonest by far is the first: a shift is longer than a session.
         */
        if (res.status === 401) {
          throw new Error('Your session has expired. Sign in again to carry on labelling.');
        }
        if (res.status >= 500) {
          throw new Error(
            `The server could not answer (${res.status}). If it was just restarted, wait a ` +
              'moment and press Reload.',
          );
        }
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message ?? `The queue could not be loaded (${res.status}).`);
      }
      const rows = await res.json();
      setQueue(rows);
      setIndex(0);
    } catch (e) {
      // A thrown TypeError here is fetch itself failing — no response at all,
      // which means the network or the service, not the request.
      setError(
        e instanceof TypeError
          ? 'Could not reach the server. It may be restarting — press Reload in a moment.'
          : (e as Error).message,
      );
    }
  }, [productId]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const current = queue[index];

  /**
   * The list as it is offered.
   *
   * Recently used first, then everything else. Not "most used ever" — a shift
   * runs a range, and yesterday's popular cut is not necessarily today's.
   */
  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const matches = classes.filter(
      (c) =>
        !needle ||
        c.code.toLowerCase().includes(needle) ||
        c.name.toLowerCase().includes(needle) ||
        (c.family ?? '').toLowerCase().includes(needle),
    );
    if (needle) return matches;
    const recentFirst = recent
      .map((id) => matches.find((c) => c.id === id))
      .filter((c): c is Klass => Boolean(c));
    const rest = matches.filter((c) => !recent.includes(c.id));
    return [...recentFirst, ...rest];
  }, [classes, filter, recent]);

  // The model's answer, preselected. Falls back to the top of the list.
  useEffect(() => {
    if (!current?.classId) {
      setHighlight(0);
      return;
    }
    const at = shown.findIndex((c) => c.id === current.classId);
    setHighlight(at >= 0 ? at : 0);
  }, [current, shown]);

  const save = async (classId: number) => {
    if (!current || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch('/api/annotations', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          imageKey: current.imageKey,
          inspectionId: current.id,
          productId,
          classId,
          regions: [],
        }),
      });
      if (!res.ok) throw new Error((await res.json()).message ?? 'Could not save');
      setRecent((r) => [classId, ...r.filter((x) => x !== classId)].slice(0, 8));
      setDone((n) => n + 1);
      setFilter('');
      // Advance rather than reload: refetching after every crate would put a
      // network round trip between the operator and the next image.
      setIndex((i) => i + 1);
      searchRef.current?.focus();
    } catch (e) {
      // Left on the same image on purpose. Skipping forward after a failed save
      // loses the label silently, which is the failure this project keeps
      // finding.
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, shown.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = shown[highlight];
      if (pick) void save(pick.id);
    } else if (e.key === 'Escape') {
      setFilter('');
    }
  };

  if (classifyProducts.length === 0) {
    return (
      <div>
        <h1 className="view-title">Classify</h1>
        <div className="panel muted">
          No classification product yet. A product records which question its line asks; set one to
          Classification in Settings, then define its classes.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <h1 className="view-title">Classify</h1>
        <div className="row" style={{ gap: 10, alignItems: 'center' }}>
          {done > 0 && (
            <span className="muted" style={{ fontSize: 13 }}>
              {done} labelled this session
            </span>
          )}
          <select value={productId ?? ''} onChange={(e) => setProductId(Number(e.target.value))}>
            {classifyProducts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="panel" style={{ color: 'var(--bad)', marginBottom: 12 }}>{error}</div>}

      {!current ? (
        <div className="panel muted">
          Nothing waiting. {queue.length > 0 ? 'You reached the end of the queue.' : ''}{' '}
          <button className="btn sm" onClick={() => void loadQueue()} style={{ marginLeft: 8 }}>
            Reload
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 360px', gap: 16 }}>
          <div className="panel" style={{ padding: 10 }}>
            <CrateImage imageKey={current.imageKey} />
            <div
              className="row"
              style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}
            >
              <span className="muted" style={{ fontSize: 12 }}>
                {index + 1} of {queue.length} in the queue
              </span>
              <button
                className="btn sm"
                onClick={() => setIndex((i) => i + 1)}
                title="Move on without labelling this one. Nothing is recorded."
              >
                Skip →
              </button>
            </div>
          </div>

          <div className="panel">
            {current.classId && (
              <div
                style={{
                  marginBottom: 12,
                  padding: '8px 10px',
                  borderRadius: 6,
                  background: 'rgba(59,130,246,.12)',
                  fontSize: 13,
                }}
              >
                Model says{' '}
                <strong>{classes.find((c) => c.id === current.classId)?.name}</strong>
                {current.classConfidence != null &&
                  ` · ${(current.classConfidence * 100).toFixed(0)}%`}
                {current.decision === 'reject' && ' — below threshold, which is why you are here'}
                <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>
                  Preselected. Enter to agree, or type to pick another.
                </div>
              </div>
            )}

            <input
              ref={searchRef}
              autoFocus
              value={filter}
              placeholder="Type to search — ↑↓ to move, Enter to save"
              onChange={(e) => {
                setFilter(e.target.value);
                setHighlight(0);
              }}
              onKeyDown={onKey}
              style={{ width: '100%', marginBottom: 8 }}
            />

            <div style={{ maxHeight: 460, overflowY: 'auto' }}>
              {shown.length === 0 ? (
                <div className="muted" style={{ fontSize: 13, padding: 10 }}>
                  No class matches “{filter}”.
                </div>
              ) : (
                shown.map((c, i) => (
                  <button
                    key={c.id}
                    onClick={() => void save(c.id)}
                    onMouseEnter={() => setHighlight(i)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      padding: '7px 10px',
                      marginBottom: 2,
                      borderRadius: 5,
                      border: '1px solid transparent',
                      background: i === highlight ? 'var(--accent-dim)' : 'transparent',
                      color: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontFamily: 'monospace', fontSize: 12, opacity: 0.7 }}>
                      {c.code}
                    </span>{' '}
                    {c.name}
                    <span className="muted" style={{ float: 'right', fontSize: 11 }}>
                      {/* How much data this class has. Visible while labelling so
                          a thin class can be fed on purpose rather than by luck. */}
                      {c.images}
                      {c.family ? ` · ${c.family}` : ''}
                    </span>
                  </button>
                ))
              )}
            </div>

            <div className="muted" style={{ fontSize: 11, marginTop: 10 }}>
              {recent.length > 0 && !filter && 'Recently used are listed first. '}
              Nothing is skipped — a failed save keeps you on the same image.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The image, fetched the same way every other view fetches one.
 *
 * A missing object is shown as a missing object, not as the browser's broken-image
 * icon. An inspection row can outlive its bytes — a bucket lifecycle rule, a
 * restore that skipped the object store, a row seeded by hand — and the operator
 * needs to know the crate cannot be judged rather than wonder whether the page is
 * loading.
 */
function CrateImage({ imageKey }: { imageKey: string }) {
  const url = useImageUrl(imageKey);
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [imageKey]);

  if (!url) return <div className="spinner" style={{ padding: 40 }}>Loading…</div>;
  if (failed) {
    return (
      <div
        className="muted"
        style={{
          aspectRatio: '1',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          border: '1px dashed var(--border)',
          borderRadius: 6,
          padding: 20,
          textAlign: 'center',
          fontSize: 13,
        }}
      >
        <span style={{ fontSize: 26 }}>⃠</span>
        The image for this inspection is not in storage.
        <code style={{ fontSize: 11, opacity: 0.7 }}>{imageKey}</code>
        <span style={{ fontSize: 12 }}>Skip it — there is nothing here to label.</span>
      </div>
    );
  }
  return (
    <img
      src={url}
      alt="crate"
      onError={() => setFailed(true)}
      style={{ width: '100%', borderRadius: 6, display: 'block' }}
    />
  );
}
